import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { foyerApi, type ContentPackageRow, type SeatUnit } from "../http/foyer";
import { friendlyError } from "../utils/apiError";
import { tokens } from "../theme";

/**
 * Choose the packages a product grants.
 *
 * ## Why this did not exist
 *
 * `setFeatureSetPackages` has been in the API client for a while and **nothing
 * ever called it**. Products arrived from the portal already carrying their
 * grants, so the screen only ever needed to DISPLAY them — and once the import
 * was cancelled, a product could be created and then never given anything. The
 * chain was: package authorable, product creatable, and no way to connect the
 * two.
 *
 * Found the same way the last three gaps were: by walking the flow rather than
 * reading the routes.
 *
 * ## What it shows, and why not just names
 *
 * A package's NAME does not tell an operator whether it is the right one —
 * `svt-channels` and `svt-channels-hd` look identical in a list. So each row
 * carries what the package actually holds: how many titles are pinned, and
 * whether a rule is carrying more on top. That is the difference between
 * picking confidently and picking twice.
 *
 * ## The save REPLACES, and the route says so
 *
 * `PUT …/feature-sets/:id` refuses a partial list precisely because a partial
 * one would silently revoke the rest. This dialog therefore holds the complete
 * set at all times: it opens from what the product already grants, and every
 * change since has been a tick.
 *
 * ⚠️ A grant pointing at a package Foyer does not hold is refused by the route
 * rather than stored. Missing ids are shown here so an operator can see what
 * they are about to lose — but they cannot be re-saved, which is the correct
 * asymmetry: a dangling grant grants nothing, silently, and the cheapest moment
 * to catch it is the one where somebody is already looking at it.
 */
export default function ProductPackagePicker({
  productId,
  productName,
  granted,
  seatUnit: initialSeatUnit,
  onClose,
  onSaved,
}: {
  productId: string;
  productName: string;
  granted: { id: string; name: string; missing: boolean }[];
  /** What a licence for this product counts. `null` = it has no amount. */
  seatUnit: SeatUnit;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  /**
   * Editable HERE because this is where somebody is already looking at the
   * product, and because the products authored before the field existed all
   * have `null` — which hides the Amount field on the licence dialog. Without
   * somewhere to correct it, those products can be sold and never quantified.
   */
  const [seatUnit, setSeatUnit] = useState<SeatUnit>(initialSeatUnit);
  const [packages, setPackages] = useState<ContentPackageRow[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(
    new Set(granted.filter((g) => !g.missing).map((g) => g.id))
  );
  const [filter, setFilter] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await foyerApi.listContentPackages();
      setPackages(res.packages);
    } catch (e) {
      setError(friendlyError(e));
      setPackages([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Granted ids Foyer no longer holds. Shown, and deliberately not saveable. */
  const dangling = granted.filter((g) => g.missing);

  const visible = useMemo(() => {
    if (!packages) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return packages;
    return packages.filter(
      (p) =>
        (p.name ?? "").toLowerCase().includes(q) ||
        p.id.toLowerCase().includes(q)
    );
  }, [packages, filter]);

  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await foyerApi.setFeatureSetPackages(productId, [...picked], seatUnit);
      await onSaved();
    } catch (e) {
      setError(friendlyError(e));
      setBusy(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={busy ? undefined : onClose}>
      <DialogTitle>
        What “{productName}” grants
        <Typography variant="body2" color="text.secondary">
          A customer who buys this product gets everything in the packages you
          tick.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <TextField
            label="What a licence counts"
            select
            size="small"
            value={seatUnit ?? "none"}
            onChange={(e) =>
              setSeatUnit(
                e.target.value === "none" ? null : (e.target.value as SeatUnit)
              )
            }
            inputProps={{ "data-testid": "product-seat-unit" }}
            helperText={
              seatUnit === null && picked.size > 0
                ? "This product grants content but counts nothing, so a licence for it cannot say how many rooms it covers."
                : "The number an operator types when they sell it."
            }
            error={seatUnit === null && picked.size > 0}
          >
            {/* Each option is the sentence an operator would say out loud.
                "No amount" read as "free"; "Nothing to count" says what it is. */}
            <MenuItem value="room">Rooms — “40 rooms of this”</MenuItem>
            <MenuItem value="screen">Screens — also the pairing cap</MenuItem>
            <MenuItem value="resident">
              Residents — a pool, never screens
            </MenuItem>
            <MenuItem value="none">
              Nothing to count — the whole place has it
            </MenuItem>
          </TextField>

          {dangling.length > 0 ? (
            <Alert severity="warning" data-testid="product-dangling">
              {dangling.length} granted package(s) no longer exist:{" "}
              {dangling.map((d) => d.id).join(", ")}. They grant nothing and
              cannot be saved again — this is where that gets tidied up.
            </Alert>
          ) : null}

          <TextField
            size="small"
            label="Filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="svt"
          />

          {packages === null ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={26} />
            </Stack>
          ) : visible.length === 0 ? (
            <Alert severity="info">
              {filter.trim()
                ? "No package matches that filter."
                : "No packages yet. Create one under Packages first — a product that grants nothing reaches nobody."}
            </Alert>
          ) : (
            <Box sx={{ maxHeight: 380, overflowY: "auto" }}>
              {visible.map((p) => (
                <Stack
                  key={p.id}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  onClick={() => toggle(p.id)}
                  data-testid={`grant-${p.id}`}
                  sx={{
                    py: 0.75,
                    px: 0.5,
                    cursor: "pointer",
                    borderBottom: 1,
                    borderColor: "divider",
                    "&:hover": { background: tokens.rowAlt },
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={picked.has(p.id)}
                    tabIndex={-1}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggle(p.id)}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" noWrap>
                      {p.name || p.id}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontFamily: "monospace", color: tokens.inkMuted }}
                      noWrap
                    >
                      {p.id}
                    </Typography>
                  </Box>
                  {/* A name does not say whether this is the right package.
                      What it HOLDS does. */}
                  <Stack direction="row" spacing={0.5}>
                    {p.assets.length > 0 ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${p.assets.length} picked`}
                      />
                    ) : null}
                    {p.rule ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="primary"
                        label={
                          p.resolved?.inert
                            ? "rule — no trusted provider"
                            : `rule — ${p.resolved?.ruleCount ?? 0}${
                                p.resolved?.truncated ? "+" : ""
                              }`
                        }
                      />
                    ) : null}
                    {p.assets.length === 0 && !p.rule ? (
                      <Chip size="small" color="warning" label="empty" />
                    ) : null}
                  </Stack>
                </Stack>
              ))}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mr: "auto", pl: 2 }}
        >
          {picked.size} package(s)
        </Typography>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void save()}
          disabled={busy}
          data-testid="product-grants-save"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
